import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Button } from '@abyss/web/ui/Button';
import { debounce } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { FilterCollectionModel } from '../../../common/Constants';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { useCoverageType } from '../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../hooks/useLagoon';
import { useParsedProviderFilters } from '../../../hooks/useProviderFilterCount';
import { useProviderFilters } from '../../../hooks/useProviderFilters';
import { useSmartChoice } from '../../../hooks/useSmartChoice';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import {
  LastModifiedFilter,
  ProviderFiltersType,
  ProviderSearchFilters,
} from '../../../models/ProviderSearch';
import { usePlanSelectorStore } from '../../../store/usePlanSelectorStore';
import { useSearchStore } from '../../../store/useSearchStore';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import {
  getLagoonConfigValue,
  resolveFilterSortOption,
  transformFiltersData,
} from '../../../utils/providerSearch.utils';
import { adobeSearchFilterEvent } from '../../AdobeTagging/adobeSearchFilterEvent';
import { mobileOnly, phoneOnly } from '../../ConstantsStyles';
import {
  getFormattedFilters,
  getSearchTerm,
} from '../../Utils/adobeTrackUtils/adobeTrackUtils';
import {
  disableDistanceFilter,
  getFilterValue,
  resolveSelectedFilters,
} from '../../Utils/filters.utils';
import {
  isFilterDisabledByBcc,
  isPrioritySchedulingActive,
} from '../BccFilterUtils';
import { setTranslationsForPreSelectedFilters } from './allFilterDrawerUtil';
import { AllFilterToggleSwitchGroup } from './AllFilterToggleSwitchGroup';
import {
  BottomSection,
  FilterWrapper,
  ScrollableSection,
  StyledDrawer,
  StyledModal,
} from './index.style';
import { ProviderFilters } from './ProviderFilters';
import { SelectedFilters } from './SelectedFilters';

type Props = {
  position: string;
  isOpen: boolean;
  size: number;
  onClose: () => void;
  allFiltersButtonRef: React.RefObject<HTMLButtonElement>;
  defaultSearchFilters: ProviderSearchFilters;
  selectedFilters: ProviderSearchFilters;
  setSelectedFilters: (data: ProviderSearchFilters) => void;
  setSearchFilters: (data: ProviderSearchFilters) => void;
  suppressPremiumCarePhysician?: boolean;
};

export const AllFilterDrawer = ({
  position,
  isOpen,
  size,
  onClose,
  allFiltersButtonRef,
  defaultSearchFilters,
  selectedFilters,
  setSelectedFilters,
  setSearchFilters,
  suppressPremiumCarePhysician = false,
}: Props) => {
  const { t } = useTranslation();
  const coverageType = useCoverageType();
  const firstFocusableElementRef = useRef<HTMLDivElement>(null);
  const [
    enableMapViewEnhancements,
    enableBHVirtualCareFilterV2,
    enableWigsFilter,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_VIRTUAL_CARE_FILTER_V2,
    ConstantsLagoon.FEATURE_FLAGS.WIGS_FILTER_ENABLE,
  ]);
  const [enableFilterAndSortCTAMapView] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_FILTER_AND_SORT_CTA_MAP_VIEW,
  ]);
  const mobileScreen = useMediaQuery(
    enableMapViewEnhancements ? mobileOnly : phoneOnly
  );

  const allConfigs = useLagoon('config');
  const defaultSearchRadius = getLagoonConfigValue(
    allConfigs,
    'DEFAULT_SEARCH_RADIUS'
  );
  const isSmartChoice = useSmartChoice();

  const [enhanceDistanceFilter] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENHANCE_DISTANCE_FILTER,
  ]);
  const planInfo = usePlanSelectorStore((state) => state.planInfo);

  const filtersCollection = useProviderFilters(enhanceDistanceFilter);

  const [lastModifiedFilter, setLastModifiedFilter] =
    useState<LastModifiedFilter>({});

  const [currentSelectedFilters, setCurrentSelectedFilters] =
    useState<ProviderSearchFilters>(selectedFilters);

  const [selectedFiltersContainerHeight, setSelectedFiltersContainerHeight] =
    useState<number>(0);
  const typeaheadState = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state.typeaheadSearchStore.search,
      sectionType: state.typeaheadSearchStore.sectionType,
      searchMethod: state.typeaheadSearchStore.searchMethod,
      searchTerm: state.typeaheadSearchStore.searchTerm,
    }))
  );
  const { sectionType = '' } = typeaheadState;

  const { setSearchStore } = useSearchStore(
    useShallow((state) => ({
      setSearchStore: state.setSearchStore,
    }))
  );

  const {
    totalCount = 0,
    providerFilterList: filtersData,
    isLoading,
  } = useParsedProviderFilters({
    selectedFilters,
  });

  const setResultsHasProviderWithUpcomingAvailability = useStore(
    StoreKeys.SET_RESULTS_HAS_PROVIDER_WITH_UPCOMING_AVAILABILITY
  );

  const ResultsHasProviderWithUpcomingAvailability = useStore(
    StoreKeys.RESULTS_HAS_PROVIDER_WITH_UPCOMING_AVAILABILITY
  );

  const setUpcomingAvailability = (filters) => {
    const hasUpcomingAvailability =
      filters?.UpcomingAvailability?.options?.length > 0;
    setResultsHasProviderWithUpcomingAvailability(hasUpcomingAvailability);
  };
  useEffect(() => {
    if (Object.keys(filtersData).length) {
      setUpcomingAvailability(filtersData);
    }
  }, [JSON.stringify(filtersData)]);
  const bhProgramLagoonData = useLagoon('behavioral-facility-programs')();
  const transformedFilters = transformFiltersData(
    t,
    filtersCollection as ProviderFiltersType[],
    filtersData,
    lastModifiedFilter,
    coverageType,
    selectedFilters,
    planInfo,
    bhProgramLagoonData
  );

  const isVirtualCareFilterSelected = getFilterValue(
    selectedFilters,
    FilterCollectionModel.VIRTUAL_CARE
  );

  // Check if Priority Scheduling is active
  const isBccActive = isPrioritySchedulingActive(selectedFilters);

  const filters = transformedFilters.map((filter) => {
    if (
      disableDistanceFilter(
        filter.model as FilterCollectionModel,
        isVirtualCareFilterSelected,
        selectedFilters,
        enhanceDistanceFilter
      )
    ) {
      filter.isDisabled = true;
    }

    // Disable filters when Priority Scheduling is active
    if (isFilterDisabledByBcc(filter.model, isBccActive)) {
      filter.isDisabled = true;
    }

    if (filter.model === FilterCollectionModel.HEALTH_SUPPLIES_OFFERED) {
      const healthSuppliesOfferedOptions = filter.options.filter((option) => {
        if (
          option.label === 'Wigs' &&
          option.value === '853' &&
          (enableWigsFilter === undefined ||
            enableWigsFilter === null ||
            !enableWigsFilter)
        ) {
          return false;
        } else {
          return true;
        }
      });
      filter.options = healthSuppliesOfferedOptions;
    }
    return filter;
  });

  useEffect(() => {
    if (
      !isLoading &&
      filters.length > 0 &&
      filtersData &&
      Object.keys(filtersData).length > 0
    ) {
      const computedSelectedFilters = setTranslationsForPreSelectedFilters(
        filters,
        selectedFilters
      );
      if (
        JSON.stringify(computedSelectedFilters) !==
        JSON.stringify(selectedFilters)
      ) {
        const resolvedSelectedFilters = resolveSelectedFilters(
          enhanceDistanceFilter,
          coverageType,
          computedSelectedFilters,
          selectedFilters,
          enableBHVirtualCareFilterV2,
          sectionType,
          defaultSearchRadius
        );
        setSelectedFilters(resolvedSelectedFilters);
      }
    }
  }, [filters.length, isLoading]);

  const applyFilterSelectionChange = (
    currentSelectedFilters: ProviderSearchFilters,
    lastModifiedModel: string = ''
  ) => {
    setLastModifiedFilter(
      filters.find((filter) => filter.model === lastModifiedModel) || {}
    );
    const resolvedSelectedFilters = resolveSelectedFilters(
      enhanceDistanceFilter,
      coverageType,
      currentSelectedFilters,
      selectedFilters,
      enableBHVirtualCareFilterV2,
      sectionType,
      defaultSearchRadius
    );
    setSelectedFilters(resolvedSelectedFilters);
  };

  const onFilterSelectionChange = debounce(applyFilterSelectionChange, 400);

  const handleSubmit = () => {
    const filters = resolveFilterSortOption(
      ResultsHasProviderWithUpcomingAvailability,
      isSmartChoice,
      selectedFilters,
      coverageType,
      t
    );
    adobeSearchFilterEvent({
      filters: getFormattedFilters(filters),
      term: getSearchTerm(typeaheadState),
    });
    setLastModifiedFilter({});

    // Update searchStore.searchRadius when filters are applied
    const distanceFilter = filters[FilterCollectionModel.DISTANCE];
    const autoSearchRadius = filters[FilterCollectionModel.AUTO_SEARCH_RADIUS];
    if (
      distanceFilter &&
      Array.isArray(distanceFilter) &&
      distanceFilter.length > 0
    ) {
      const distanceValue = distanceFilter[0]?.value;

      if (
        distanceValue !== undefined &&
        distanceValue !== null &&
        typeof distanceValue === 'number' &&
        distanceValue >= 0
      ) {
        setSearchStore({ searchRadius: distanceValue });
      }
    } else if (
      autoSearchRadius !== undefined &&
      autoSearchRadius !== null &&
      typeof autoSearchRadius === 'number' &&
      autoSearchRadius > 0
    ) {
      setSearchStore({ searchRadius: autoSearchRadius });
    }

    setSearchFilters(filters);
    onClose();
  };

  const handleCloseAllFilterDrawer = () => {
    setSelectedFilters(currentSelectedFilters);
    onClose();
    if (allFiltersButtonRef.current) {
      allFiltersButtonRef.current.focus();
    }
  };


  // unsure why linter is complaining about no return for a useEffect function
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isOpen) {
      setCurrentSelectedFilters(selectedFilters);
      const previousActiveElement = document.activeElement as HTMLElement;
      if (firstFocusableElementRef.current) {
        firstFocusableElementRef.current.focus();
      }
      return () => {
        if (allFiltersButtonRef?.current) {
          allFiltersButtonRef.current.focus();
        }
        previousActiveElement.focus();
      };
    }
  }, [isOpen, allFiltersButtonRef]);

  const commonProps = {
    'data-auto-testid': 'all-filter-drawer',
    'data-testid': 'all-filter-drawer',
    mapViewEnhancement: enableMapViewEnhancements,
    footer: (
      <BottomSection cssProps={{ enableMapViewEnhancements, mobileScreen }}>
        <Button
          css={{
            fontWeight: '$semibold',
            '@screen < $sm': {
              marginBottom: mobileScreen ? '0px' : '70px',
            },
            position: 'relative',
            zIndex: '999999999 !important',
          }}
          data-auto-testid="show-result"
          data-testid="show-result"
          isDisabled={isLoading || totalCount === 0}
          onClick={handleSubmit}
          ref={allFiltersButtonRef}
        >
          {!isLoading
            ? t(
                'CONTENT_FILTERS.ALL_FILTERS_DRAWER.SHOW_RESULT_BUTTON'
              ).replace('$count', totalCount?.toString())
            : t('CONTENT_FILTERS.ALL_FILTERS_DRAWER.LOADING')}
        </Button>
      </BottomSection>
    ),
    isOpen,
    onClose: handleCloseAllFilterDrawer,
    position,
    ref: firstFocusableElementRef,
    size,
    cssProps: { mobileScreen },
    title: t('CONTENT_FILTERS.ALL_FILTERS'),
  };

  const commonContent = (
    <React.Fragment>
      <SelectedFilters
        defaultSearchFilters={defaultSearchFilters}
        lastModifiedFilter={lastModifiedFilter}
        onFilterSelectionChange={onFilterSelectionChange}
        selectedFilters={selectedFilters}
        setSelectedFiltersContainerHeight={setSelectedFiltersContainerHeight}
      />

      <FilterWrapper className="hideScrollBar">
        <ScrollableSection>
          <AllFilterToggleSwitchGroup
            providerFilters={filters}
            sectionType={sectionType}
            selectedFilters={selectedFilters}
            selectedFiltersContainerHeight={selectedFiltersContainerHeight}
            setSelectedFilters={applyFilterSelectionChange}
            suppressPremiumCarePhysician={suppressPremiumCarePhysician}
          />
          <ProviderFilters
            isData={!isLoading}
            onFilterSelectionChange={onFilterSelectionChange}
            providerFilters={filters}
            sectionType={sectionType}
            selectedFilters={selectedFilters}
          />
        </ScrollableSection>
      </FilterWrapper>
    </React.Fragment>
  );

  if (enableFilterAndSortCTAMapView) {
    return <StyledDrawer {...commonProps}>{commonContent}</StyledDrawer>;
  }

  return <StyledModal {...commonProps}>{commonContent}</StyledModal>;
};
